import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Delete } from '@mui/icons-material';

import { BiConditionOperatorList } from '../../../config/bi_report';
import { BI_CONDITION_VALUELESS_OPERATORS } from '../../../utils/biCondition';
import { compileBiFormula } from '../../../utils/biFormula';

const SOURCE_TYPE = {
  FIELD: 'FIELD',
  FORMULA: 'FORMULA',
};

const createCondition = (sortOrder) => ({
  sortOrder,
  operator: '',
  compareValue: '',
  resultValue: '',
  isDefault: false,
});

const BiConditionDialog = ({ open, onClose, biField, availableFields, onSave }) => {
  const [sourceType, setSourceType] = useState(SOURCE_TYPE.FIELD);
  const [field, setField] = useState(null);
  const [formula, setFormula] = useState('');
  const [conditions, setConditions] = useState([]);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) return;

    setSourceType(biField?.fnctn ? SOURCE_TYPE.FORMULA : SOURCE_TYPE.FIELD);
    setField(biField?.field ?? null);
    setFormula(biField?.fnctn ?? '');
    setConditions(
      (biField?.biFieldCondition ?? []).map((condition, index) => ({
        ...condition,
        sortOrder: index + 1,
      }))
    );
    setValidationError('');
  }, [open, biField]);

  const updateCondition = (index, changes) => {
    setConditions((current) =>
      current.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...changes } : condition
      )
    );
  };

  const removeCondition = (index) => {
    setConditions((current) =>
      current.filter((_condition, conditionIndex) => conditionIndex !== index).map((condition, conditionIndex) => ({
        ...condition,
        sortOrder: conditionIndex + 1,
      }))
    );
  };

  const moveCondition = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= conditions.length) return;

    setConditions((current) => {
      const reordered = [...current];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      return reordered.map((condition, conditionIndex) => ({ ...condition, sortOrder: conditionIndex + 1 }));
    });
  };

  const validate = () => {
    if (sourceType === SOURCE_TYPE.FIELD && !field) return 'A vizsgált mező megadása kötelező!';
    if (sourceType === SOURCE_TYPE.FORMULA) {
      try {
        compileBiFormula(formula, availableFields.map((item) => item.value));
      } catch (error) {
        return error.message;
      }
    }
    if (!conditions.length) return 'Legalább egy feltétel megadása kötelező!';
    if (conditions.filter((condition) => condition.isDefault).length > 1) {
      return 'Csak egy alapértelmezett ág adható meg!';
    }

    for (const condition of conditions) {
      if (!condition.resultValue && condition.resultValue !== 0) return 'Minden feltételhez kimenet szükséges!';
      if (condition.isDefault) continue;
      if (!condition.operator) return 'Minden feltételhez operátor szükséges!';
      if (
        !BI_CONDITION_VALUELESS_OPERATORS.includes(condition.operator) &&
        condition.compareValue === ''
      ) {
        return 'Az összehasonlítási érték megadása kötelező!';
      }
    }

    return '';
  };

  const handleSave = () => {
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }

    onSave({
      field: sourceType === SOURCE_TYPE.FIELD ? field : null,
      fnctn: sourceType === SOURCE_TYPE.FORMULA ? formula : '',
      biFieldCondition: conditions.map((condition, index) => ({
        ...condition,
        sortOrder: index + 1,
        operator: condition.isDefault ? null : condition.operator,
        compareValue:
          condition.isDefault || BI_CONDITION_VALUELESS_OPERATORS.includes(condition.operator)
            ? null
            : condition.compareValue,
      })),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Feltételes mező szerkesztése</DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <TextField
            select
            label="Forrás típusa"
            value={sourceType}
            onChange={(event) => setSourceType(event.target.value)}
            variant="standard"
          >
            <MenuItem value={SOURCE_TYPE.FIELD}>Mező</MenuItem>
            <MenuItem value={SOURCE_TYPE.FORMULA}>Képlet</MenuItem>
          </TextField>
          {sourceType === SOURCE_TYPE.FIELD ? (
            <Autocomplete
              options={availableFields}
              value={availableFields.find((item) => item.value === field) ?? null}
              getOptionLabel={(option) => option.label ?? ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_event, value) => setField(value?.value ?? null)}
              renderInput={(params) => <TextField {...params} label="Vizsgált mező" variant="standard" />}
            />
          ) : (
            <TextField
              label="Képlet"
              value={formula}
              onChange={(event) => setFormula(event.target.value)}
              placeholder="Pl.: ([ELADOTT] / [ÖSSZES]) * 100"
              variant="standard"
            />
          )}
        </div>

        <div className="flex items-center justify-between">
          <Typography variant="h6">Feltételek</Typography>
          <Button
            type="button"
            startIcon={<Add />}
            onClick={() => setConditions((current) => [...current, createCondition(current.length + 1)])}
          >
            Feltétel hozzáadása
          </Button>
        </div>

        {conditions.map((condition, index) => (
          <div
            key={condition.id ?? index}
            className={`grid grid-cols-12 gap-3 items-center rounded px-3 py-2 ${index % 2 === 0 ? 'bg-gray-100' : ''}`}
          >
            <div className="col-span-1 flex items-center">
              <Typography>{index + 1}.</Typography>
              <div className="flex flex-col">
                <IconButton size="small" disabled={index === 0} onClick={() => moveCondition(index, -1)}>
                  <ArrowUpward fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === conditions.length - 1}
                  onClick={() => moveCondition(index, 1)}
                >
                  <ArrowDownward fontSize="inherit" />
                </IconButton>
              </div>
            </div>
            <div className="col-span-3">
              <TextField
                select
                fullWidth
                label="Operátor"
                value={condition.operator ?? ''}
                disabled={condition.isDefault}
                onChange={(event) => updateCondition(index, { operator: event.target.value })}
                variant="standard"
              >
                {BiConditionOperatorList.map((operator) => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div className="col-span-3">
              <TextField
                fullWidth
                label="Összehasonlítási érték"
                value={condition.compareValue ?? ''}
                disabled={condition.isDefault || BI_CONDITION_VALUELESS_OPERATORS.includes(condition.operator)}
                onChange={(event) => updateCondition(index, { compareValue: event.target.value })}
                variant="standard"
              />
            </div>
            <div className="col-span-3">
              <TextField
                fullWidth
                label="Kimenet"
                value={condition.resultValue ?? ''}
                onChange={(event) => updateCondition(index, { resultValue: event.target.value })}
                variant="standard"
              />
            </div>
            <div className="col-span-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(condition.isDefault)}
                    onChange={(event) => updateCondition(index, { isDefault: event.target.checked })}
                  />
                }
                label="Alapértelmezett"
              />
            </div>
            <div className="col-span-1 flex justify-end">
              <IconButton color="error" onClick={() => removeCondition(index)}>
                <Delete />
              </IconButton>
            </div>
          </div>
        ))}

        {!conditions.length && <Typography color="text.secondary">Még nincs rögzített feltétel.</Typography>}
        {validationError && <Typography color="error">{validationError}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Mégse</Button>
        <Button variant="contained" onClick={handleSave}>
          Mentés
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BiConditionDialog;
